import React, { useEffect, useRef, useState } from 'react'
import InView from 'react-intersection-observer'
import BarChart from '../BarChart'
import { useTranslation } from 'gatsby-plugin-react-i18next'

import * as styles from './styles.module.scss'
import * as chartStyles from '../BarChart/styles.module.scss'
import { animated, useTransition } from 'react-spring'

interface FlowerBud {
	title: string
	percent: number
	className: string
	overlayClassName: string
	titleClassName?: string
	animationDelay?: number
}

const _flowerBuds: FlowerBud[] = [
	{
		title: 'perfumes',
		percent: 50,
		className: styles.flowerPerfumes,
		overlayClassName: styles.flowerOverlayPink,
		titleClassName: 'h2',
		animationDelay: 0
	},
	{
		title: 'hair',
		percent: 14,
		className: styles.flowerHair,
		overlayClassName: styles.flowerOverlayBlack,
		animationDelay: 2
	},
	{
		title: 'skin',
		percent: 13,
		className: styles.flowerSkin,
		overlayClassName: styles.flowerOverlayGray1,
		animationDelay: 4
	},
	{
		title: 'makeup',
		percent: 9,
		className: styles.flowerMakeup,
		overlayClassName: styles.flowerOverlayGray2,
		animationDelay: 6
	},
	{
		title: 'others',
		percent: 16,
		className: styles.flowerOthers,
		overlayClassName: styles.flowerOverlayGray3,
		animationDelay: 8
	}
]

const Customers: React.FC = () => {
	const [showCharts, setShowCharts] = useState(false)
	const { t } = useTranslation()
	const ref = useRef(null)
	const [addShakeAnimation, setShakeAnimation] = useState(false)

	const [flowerBuds, setFlowerBuds] = useState<FlowerBud[]>([])

	const transitions = useTransition(flowerBuds, {
		from: { opacity: 0.5, transform: 'scale(0)' },
		enter: { opacity: 1, transform: 'scale(1)' },
		delay: 1000,
		trail: 400,
		config: { mass: 1, tension: 400, friction: 30 },
		onRest: () => setShakeAnimation(true)
	})

	return (
		<section>
			<div className="container">
				<InView onChange={(inView) => inView && setShowCharts(true)}>
					<div className="row">
						<div className="col-md-6 my-5 d-flex flex-column">
							<h2 className="text-center mb-3">20 000 000</h2>
							<div className="px-lg-5 flex-grow-1 d-flex align-items-center flex-column">
								<div className={chartStyles.chartPlaceholder}>
									{showCharts && (
										<BarChart
											data={[
												{
													category: '2018',
													value: 10400000,
													label: '10,4 mil.'
												},
												{
													category: '2019',
													value: 13000000,
													label: '13 mil.'
												},
												{
													category: '2020',
													value: 16500000,
													label: '16,5 mil.'
												},
												{
													category: '2021',
													value: 20000000,
													label: '20 mil.'
												}
											]}
										/>
									)}
								</div>
								<div className="text-center mb-7">
									<p className="h4 mb-0">{t('customers.text')}</p>
								</div>
							</div>
						</div>
						<div className="col-md-6 my-5">
							<h2 className="text-center mb-3" ref={ref}>
								{t('customers.chart2.title')}
							</h2>
							<div className="px-lg-5 text-center">
								<InView
									onChange={(inView) => inView && setFlowerBuds(_flowerBuds)}
								>
									<div className={styles.flowerContainer}>
										{transitions((style, flowerBud) => (
											<animated.div
												className={`${styles.flowerBud} ${flowerBud.className}`}
												style={{
													...style,
													backgroundImage: `url(/2021/images/customers/${flowerBud.title}.jpg)`,
													animation: addShakeAnimation
														? `shake 40s ${flowerBud.animationDelay}s`
														: undefined
												}}
											>
												<div
													className={`${styles.flowerOverlay} ${flowerBud.overlayClassName}`}
												/>
												<div className="position-relative">
													<div
														className={`text-white mb-1 ${
															flowerBud.titleClassName ?? 'h3'
														}`}
													>
														{t(`customers.chart2.labels.${flowerBud.title}`)}
													</div>
													<div className="h4 font-weight-bold text-white mb-0">
														{flowerBud.percent}&nbsp;%
													</div>
												</div>
											</animated.div>
										))}
									</div>
								</InView>
							</div>
						</div>
					</div>
				</InView>
			</div>
		</section>
	)
}

export default Customers
